import React, { FC, useContext, useState } from 'react';
import { Row, Col } from 'styled-bootstrap-grid';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import CSS from 'csstype';
import { DragDropContext, Draggable, DroppableStateSnapshot, Droppable, DropResult } from '@hello-pangea/dnd';

import { Course, Player } from '../../../../../database.schema';
import { UpdateButton } from './update-button';
import { useFetchOnTourPlayers } from '../../../../api/hooks/use-fetch-players';
import { SelectedIndexContext } from '../../../../App';
import { Button, FlexRowStart } from '../../../common-styles/common-styles';
import { MatchEntryInput, MatchEntryLabel } from '../../match-entry-styles';
import { MATCH_FORMATS } from '../../../../app/constants';
import { TeamDropBoxes } from './team-drop-boxes';
import { SinglesDropBoxes } from './singles-drop-boxes';
import { PairsDropBoxes } from './pairs-drop-boxes';

const PlayerX = styled.div`
  height: 2rem;
  padding: 5px;
  width: 8rem;
  background: darkgrey;
  text-align: center;
  border: 1px solid grey;
  border-radius: 0.25rem;
  margin: 0.25rem;
`;
const PlayersWrapper = styled.div`
  border: 1px solid black;
  border-radius: 0.25rem;
  width: 11rem;
  margin: 0.5rem auto;
  background-color: transaparent;
  color: white;
  padding: 5px;
  min-height: 2rem;
`;

const ButtonWrapper = styled.div`
  margin-top: 2rem;
  display: flex;
  justify-content: space-evenly;
`;

const SelectWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  margin: 0.5rem 0;
  select {
    height: 1.5rem;
    margin-left: 0.25rem;
  }
  label {
    margin-right: 0.5rem;
  }
`;

const style = (snapshot: DroppableStateSnapshot): CSS.Properties => ({
  padding: '0.5rem 1rem',
  minHeight: '4rem',
  background: 'lightcoral',
  borderRadius: '0.25rem',
});

const getPlayersAlphaSort = (players: Array<Player>): Array<Player> => {
  if (players.length) {
    return [...players].sort((a: Player, b: Player) => {
      if (a.nickName < b.nickName) {
        return -1;
      }
      if (a.nickName > b.nickName) {
        return 1;
      }
      return 0;
    });
  }
  return [];
};

type TierMatchEntryProps = {
  nextMatchIndex: number;
  courses: Array<Course>;
};
export const TeamGameMatchEntry: FC<TierMatchEntryProps> = ({ nextMatchIndex, courses }): JSX.Element => {
  const navigate = useNavigate();
  const [updateStatus, setUpdateStatus] = useState({ success: false, error: false });
  const { selectedTournamentIndex } = useContext(SelectedIndexContext);
  const { isLoading, data } = useFetchOnTourPlayers();

  const [players, setPlayers] = useState<Array<Player>>(getPlayersAlphaSort(data as Array<Player>));
  const [team1, setTeam1] = useState<Array<Player>>([]);
  const [team2, setTeam2] = useState<Array<Player>>([]);
  const [startingHole, setStartingHole] = useState<number>(-1);
  const [courseId, setCourseId] = useState<number>(-1);
  const [matchFormat, setMatchFormat] = useState<string>('');

  if (isLoading) {
    return <div>loading...</div>;
  }
  const isValid = () => {
    return !(team1.length > 2 && team2.length > 2 && team1.length === team2.length && courseId > -1 && startingHole > -1 && matchFormat !== '');
  };
  const onDragEnd = (result: DropResult): void => {
    if (!result.destination) {
      return;
    }
    if (result.source.droppableId === result.destination.droppableId) {
      return;
    }

    const dragPlayer = players.find((player: Player) => player.id === Number(result.draggableId));
    if (result.destination.droppableId === 'team1') {
      if (dragPlayer) {
        setTeam1([...team1, dragPlayer]);
      }
    }
    if (result.destination.droppableId === 'team2') {
      if (dragPlayer) {
        setTeam2([...team2, dragPlayer]);
      }
    }
    if (result.destination.droppableId === 'players') {
      if (dragPlayer) {
        setPlayers([...players, dragPlayer]);
      }
    }

    if (result.source.droppableId === 'players') {
      const newState = [...players];
      newState.splice(result.source.index, 1);
      setPlayers(newState);
    }
    if (result.source.droppableId === 'team1') {
      const newState = [...team1];
      newState.splice(result.source.index, 1);
      setTeam1(newState);
    }
    if (result.source.droppableId === 'team2') {
      const newState = [...team2];
      newState.splice(result.source.index, 1);
      setTeam2(newState);
    }
    if (result.destination.droppableId.startsWith('team1Single')) {
      const dragPlayer = players.find((player) => player.id === Number(result.draggableId));
      if (dragPlayer) {
        setTeam1([...team1, dragPlayer]);
      }
    }
    if (result.destination.droppableId.startsWith('team2Single')) {
      const dragPlayer = players.find((player) => player.id === Number(result.draggableId));
      if (dragPlayer) {
        setTeam2([...team2, dragPlayer]);
      }
    }
    if (result.destination.droppableId.startsWith('team1Pair')) {
      const dragPlayer = players.find((player) => player.id === Number(result.draggableId));
      if (dragPlayer) {
        setTeam1([...team1, dragPlayer]);
      }
    }
    if (result.destination.droppableId.startsWith('team2Pair')) {
      const dragPlayer = players.find((player) => player.id === Number(result.draggableId));
      if (dragPlayer) {
        setTeam2([...team2, dragPlayer]);
      }
    }
  };
  return (
    <DragDropContext onDragEnd={onDragEnd}>
      <h1>Create Teams</h1>
      <FlexRowStart>
        <SelectWrapper>
          <div>
            <strong>Course:</strong>
            <select data-testid="course" name="course" defaultValue={-1} onChange={(e) => setCourseId(Number(e.target.value))}>
              <option value="">Course...</option>
              {courses.map((aCourse: Course) => (
                <option key={aCourse.name} value={`${aCourse.id}`}>
                  {aCourse.name}
                </option>
              ))}
            </select>
          </div>
        </SelectWrapper>
      </FlexRowStart>
      <FlexRowStart>
        <strong>Starting Hole:</strong>
        <MatchEntryInput
          type="radio"
          name="startingHole"
          value="1"
          id="1"
          data-testid="first"
          checked={startingHole === 1}
          onChange={() => {
            setStartingHole(1);
          }}
        />
        <MatchEntryLabel htmlFor="1">First</MatchEntryLabel>
        <MatchEntryInput
          type="radio"
          name="startingHole"
          value="10"
          id="10"
          data-testid="tenth"
          checked={startingHole === 10}
          onChange={() => {
            setStartingHole(10);
          }}
        />

        <MatchEntryLabel htmlFor="10">Tenth</MatchEntryLabel>
      </FlexRowStart>
      <FlexRowStart>
        <SelectWrapper>
          <div>
            <strong>Match type:</strong>
            <select data-testid="format" name="matchFormat" defaultValue="" onChange={(e) => setMatchFormat(e.target.value)}>
              <option value="">Match...</option>
              <option value={MATCH_FORMATS.tierPoints_6_6_6.id}>{MATCH_FORMATS.tierPoints_6_6_6.name}</option>
              <option value={MATCH_FORMATS.tierPoints_1_2_3.id}>{MATCH_FORMATS.tierPoints_1_2_3.name}</option>
              <option value={MATCH_FORMATS.singles.id}>{MATCH_FORMATS.singles.name}</option>
              <option value={MATCH_FORMATS.pairs.id}>{MATCH_FORMATS.pairs.name}</option>
              <option value={MATCH_FORMATS.stableford.id}>{MATCH_FORMATS.stableford.name}</option>
            </select>
          </div>
        </SelectWrapper>
      </FlexRowStart>
      <h2>Players</h2>
      <Row>
        <PlayersWrapper>
          <Droppable droppableId="players">
            {(provided, snapshot): JSX.Element => (
              <div {...provided.droppableProps} ref={provided.innerRef} style={style(snapshot)}>
                {players.map((player, index) => (
                  <Draggable data-testid={`player-${index}`} key={`state-${player.id}`} draggableId={`${player.id}`} index={index} isDragDisabled={false}>
                    {(provided, snapshot): JSX.Element => (
                      <PlayerX ref={provided.innerRef} {...provided.draggableProps} {...provided.dragHandleProps}>
                        {player.nickName}
                      </PlayerX>
                    )}
                  </Draggable>
                ))}
                {provided.placeholder}
              </div>
            )}
          </Droppable>
        </PlayersWrapper>
      </Row>
      {(matchFormat === MATCH_FORMATS.tierPoints_6_6_6.id || matchFormat === MATCH_FORMATS.tierPoints_1_2_3.id) && <TeamDropBoxes team1={team1} team2={team2} />}
      {matchFormat === MATCH_FORMATS.singles.id && <SinglesDropBoxes numberOfSinglesMatches={[0, 1, 2, 3]} team1Players={team1} team2Players={team2} />}
      {matchFormat === MATCH_FORMATS.pairs.id && <PairsDropBoxes team1Players={team1} team2Players={team2} />}
      {matchFormat === MATCH_FORMATS.stableford.id && <TeamDropBoxes team1={team1} team2={team2} />}
      {updateStatus.error && <div>Update failed!!!!</div>}
      <ButtonWrapper>
        <UpdateButton
          selectedTournamentIndex={selectedTournamentIndex}
          team1={team1}
          team2={team2}
          nextMatchIndex={nextMatchIndex}
          onSuccess={() => navigate('/add-team-game-results')}
          setUpdateStatus={setUpdateStatus}
          courseId={courseId}
          startingHole={startingHole}
          matchFormat={matchFormat}
          disabled={isValid()}
        />
        <Button onClick={() => navigate('/')}>Cancel</Button>
      </ButtonWrapper>
    </DragDropContext>
  );
};
